/**
 * Sanitizes user input to prevent XSS attacks
 * @param {string} input - The input to sanitize
 * @returns {string} - The sanitized input
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return '';
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;')
    .trim();
};

/**
 * Validates file type and size
 * @param {File} file - The file to validate
 * @param {string[]} allowedTypes - Array of allowed MIME types
 * @param {number} maxSize - Maximum file size in bytes
 * @returns {boolean} - Whether the file is valid
 */
export const validateFile = (file, allowedTypes = ['audio/webm', 'audio/mpeg', 'audio/wav'], maxSize = 10 * 1024 * 1024) => {
  if (!file) return false;
  if (!allowedTypes.includes(file.type)) return false;
  if (file.size > maxSize) return false;
  return true;
};

/**
 * Validates email format
 * @param {string} email - Email to validate
 * @returns {boolean} - Whether the email is valid
 */
export const validateEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates password strength
 * @param {string} password - Password to validate
 * @returns {boolean} - Whether the password is strong enough
 */
export const validatePassword = (password) => {
  // At least 8 characters, 1 uppercase, 1 lowercase, 1 number
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d]{8,}$/;
  return passwordRegex.test(password);
};

/**
 * Sanitizes file name
 * @param {string} filename - The filename to sanitize
 * @returns {string} - The sanitized filename
 */
export const sanitizeFilename = (filename) => {
  return filename
    .replace(/[^a-zA-Z0-9.-]/g, '_')
    .replace(/^\.+/, '') // Remove leading dots
    .replace(/\.{2,}/g, '.') // Replace multiple dots with single dot
    .toLowerCase();
};

/**
 * Generates a secure random string
 * @param {number} length - Length of the string
 * @returns {string} - The generated string
 */
export const generateSecureString = (length = 32) => {
  const array = new Uint8Array(length);
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

/**
 * Encrypts sensitive data
 * @param {string} data - Data to encrypt
 * @returns {string} - Encrypted data
 */
export const encryptData = async (data) => {
  const encoder = new TextEncoder();
  const dataBuffer = encoder.encode(data);
  const key = await window.crypto.subtle.generateKey(
    {
      name: 'AES-GCM',
      length: 256
    },
    true,
    ['encrypt', 'decrypt']
  );
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const encryptedData = await window.crypto.subtle.encrypt(
    {
      name: 'AES-GCM',
      iv
    },
    key,
    dataBuffer
  );
  return {
    encrypted: Array.from(new Uint8Array(encryptedData)),
    iv: Array.from(iv),
    key
  };
};

/**
 * Decrypts encrypted data
 * @param {Object} encryptedData - The encrypted data object
 * @returns {string} - Decrypted data
 */
export const decryptData = async (encryptedData) => {
  const { encrypted, iv, key } = encryptedData;
  const decrypted = await window.crypto.subtle.decrypt(
    {
      name: 'AES-GCM',
      iv: new Uint8Array(iv)
    },
    key,
    new Uint8Array(encrypted)
  );
  return new TextDecoder().decode(decrypted);
};

/**
 * Implements rate limiting for API calls
 * @param {Function} fn - Function to rate limit
 * @param {number} limit - Number of calls allowed
 * @param {number} interval - Time interval in milliseconds
 * @returns {Function} - Rate limited function
 */
export const rateLimit = (fn, limit, interval) => {
  let calls = [];
  return async (...args) => {
    const now = Date.now();
    calls = calls.filter(time => now - time < interval);
    if (calls.length >= limit) {
      throw new Error('Rate limit exceeded');
    }
    calls.push(now);
    return fn(...args);
  };
}; 